// Cuadro «Tendencia día por día» — barras apiladas SureKeep / otras financieras / contado.
//
// Calcado del mockup `portal-admin-sk-originacion.html`: mismo viewBox 800×200, mismos paddings,
// cinco líneas de reja y la misma paleta. El mock lo dibuja con `<rect>` apilados —SK abajo—, no con
// un área: acá se hace igual.
import type { OriginationDailyPoint } from '@/api/services/retail-wizard/backoffice';
import {
    formatAxisDay,
    formatBusinessDay,
    formatUnits,
    moneyShort,
    trendMax,
    trendValue,
    type TrendMode,
} from '../lib/originationMetrics';

const WIDTH = 800;
const HEIGHT = 200;
const PAD_LEFT = 44;
const PAD_RIGHT = 8;
const PAD_TOP = 8;
const PAD_BOTTOM = 22;

const INNER_WIDTH = WIDTH - PAD_LEFT - PAD_RIGHT;
const INNER_HEIGHT = HEIGHT - PAD_TOP - PAD_BOTTOM;

/** Los tres canales con la paleta del mock. El orden es el de apilado: SK abajo. */
export const TREND_CHANNELS = [
    {
        key: 'sureKeep',
        label: 'SureKeep',
        fill: 'hsl(217 91% 55%)',
        opacity: 0.32,
        stroke: 'hsl(217 91% 45%)',
        swatch: 'hsl(217 91% 55% / 0.32)',
    },
    {
        key: 'otherFinanciers',
        label: 'Otras financieras',
        fill: 'hsl(28 86% 60%)',
        opacity: 0.32,
        stroke: 'hsl(28 86% 45%)',
        swatch: 'hsl(28 86% 60% / 0.32)',
    },
    {
        key: 'cash',
        label: 'Contado',
        fill: 'hsl(215 16% 65%)',
        opacity: 0.28,
        stroke: 'hsl(215 16% 55%)',
        swatch: 'hsl(215 16% 65% / 0.28)',
    },
] as const;

/** El valor del eje: piezas enteras o dinero abreviado, según el toggle. */
function axisLabel(value: number, mode: TrendMode): string {
    return mode === 'units' ? String(Math.round(value)) : moneyShort(value);
}

function cellLabel(value: number, mode: TrendMode): string {
    return mode === 'units' ? `${formatUnits(value)} créd` : moneyShort(value);
}

export function OriginationTrendChart({
    daily,
    mode,
}: {
    daily: OriginationDailyPoint[];
    mode: TrendMode;
}) {
    const max = trendMax(daily, mode);
    const slot = INNER_WIDTH / Math.max(1, daily.length);
    const barWidth = slot * 0.75;
    const gap = slot * 0.25;

    // Con muchos días no cabe una etiqueta por barra: se pinta una de cada N, y siempre la última.
    const labelEvery = Math.max(1, Math.ceil(daily.length / 10));

    return (
        <svg
            viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`}
            preserveAspectRatio="none"
            className="block min-h-0 w-full flex-1"
            role="img"
            aria-label={`Tendencia de originación de los últimos ${String(daily.length)} días, partida en SureKeep, otras financieras y contado`}
        >
            {[0, 1, 2, 3, 4].map((step) => {
                const y = PAD_TOP + (INNER_HEIGHT * step) / 4;
                const value = max - (max * step) / 4;
                return (
                    <g key={step}>
                        <line
                            x1={PAD_LEFT}
                            y1={y}
                            x2={WIDTH - PAD_RIGHT}
                            y2={y}
                            stroke="hsl(var(--border))"
                            strokeDasharray="2 3"
                            opacity={0.7}
                        />
                        <text
                            x={PAD_LEFT - 4}
                            y={y + 3}
                            textAnchor="end"
                            fontSize={10}
                            fill="hsl(var(--muted-foreground))"
                        >
                            {axisLabel(value, mode)}
                        </text>
                    </g>
                );
            })}

            {daily.map((point, index) => {
                const x = PAD_LEFT + index * slot + gap / 2;
                const total = mode === 'units' ? point.total.units : point.total.cents;
                let cursor = PAD_TOP + INNER_HEIGHT;
                const bars = TREND_CHANNELS.map((channel) => {
                    const value = trendValue(point, channel.key, mode);
                    const height = (value / max) * INNER_HEIGHT;
                    cursor -= height;
                    return { channel, value, height, y: cursor };
                });
                const showLabel =
                    daily.length <= 14 || index % labelEvery === 0 || index === daily.length - 1;
                return (
                    <g key={point.day}>
                        <title>
                            {`${formatBusinessDay(point.day)} · ${cellLabel(total, mode)} (${bars
                                .map((bar) => `${bar.channel.label} ${cellLabel(bar.value, mode)}`)
                                .join(' · ')})`}
                        </title>
                        {bars.map((bar) => (
                            <rect
                                key={bar.channel.key}
                                x={x}
                                y={bar.y}
                                width={barWidth}
                                height={bar.height}
                                fill={bar.channel.fill}
                                fillOpacity={bar.channel.opacity}
                                stroke={bar.channel.stroke}
                                strokeWidth={1}
                            />
                        ))}
                        {showLabel ? (
                            <text
                                x={x + barWidth / 2}
                                y={HEIGHT - 6}
                                textAnchor="middle"
                                fontSize={10}
                                fill="hsl(var(--muted-foreground))"
                            >
                                {formatAxisDay(point.day)}
                            </text>
                        ) : null}
                    </g>
                );
            })}
        </svg>
    );
}

/** La leyenda del pie del cuadro, con los mismos swatches que las barras. */
export function OriginationTrendLegend() {
    return (
        <div className="flex flex-none gap-3 border-t border-border/50 px-3 pb-1.5 pt-1 text-[10px] text-muted-foreground">
            {TREND_CHANNELS.map((channel) => (
                <span key={channel.key} className="inline-flex items-center gap-1">
                    <span
                        className="inline-block size-2.5 rounded-[2px] border"
                        style={{ backgroundColor: channel.swatch, borderColor: channel.stroke }}
                        aria-hidden
                    />
                    {channel.label}
                </span>
            ))}
        </div>
    );
}
